'use client';

import { useEffect, useState, useCallback, useRef } from 'react';
import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust';
import { AnalyticsBrowser, AnalyticsBrowserSettings, Options } from '@segment/analytics-next';
import { logger } from '../utils/logger/browser';
import getIsCrawler from '../utils/getIsCrawler';
import { cdnSettings } from '@material-bank/mtag-integrations/src/config';
import { useConfigProviderSafe } from '@material-bank/mtag-config';
import { getCurrentPageType } from '../config/pageConfig';
import { gtmDestinationMiddleware } from '../utils/gtmMiddleware';

// Permissive so callers can pass typed canonical payloads (z.infer object types have no
// index signature). Payload correctness is enforced by the per-event Zod schemas, not here.
type AnalyticsProperties = Record<string, unknown>;
type AnalyticsTraits = Record<string, unknown>;
const analyticsSettings: AnalyticsBrowserSettings = {
  writeKey: 'No key is needed',
  // we are not using Segment's hosted endpoint
  cdnSettings
};

// Singleton pattern for analytics instance with promise-based initialization
let analyticsInstance: AnalyticsBrowser | null = null;
let initPromise: Promise<void> | null = null;
const initializeAnalyticsOnce = async (): Promise<void> => {
  // If initialization is already in progress, wait for it
  if (initPromise) {
    return initPromise;
  }

  // If already initialized, return immediately
  if (analyticsInstance) {
    return Promise.resolve();
  }

  // Create and store the initialization promise
  initPromise = (async () => {
    const isCrawler = getIsCrawler();
    try {
      if (isCrawler) {
        logger.warn('Segment analytics disabled for crawlers');
        return;
      }
      const {
        protocol,
        host
      } = new URL(window.location.origin);
      analyticsInstance = AnalyticsBrowser.load(analyticsSettings, {
        integrations: {
          'Segment.io': {
            protocol: protocol === 'http:' ? 'http' : 'https',
            apiHost: `${host}/rest/v1/analytics`
          }
        }
      });
      analyticsInstance = withOneTrust(analyticsInstance, {
        consentModel: () => 'opt-in'
      });

      // Stamp the surface onto `context.page` for every call — track, page and identify
      // alike. No event schema declares `page_type`, so `context.page` is where it has to
      // live, and it is where MB3 puts it. Read from the live location rather than a React
      // prop so a call fired mid-navigation reports the route it actually happened on.
      //
      // Deliberately not awaited: `addSourceMiddleware` only settles once the Segment
      // CDN script has loaded, so awaiting it would hold `isReady` — and with it every
      // track/page call, which no-op while false — behind that round-trip. Registration
      // is buffered in order, so calls made after this line are still processed by the
      // middleware. One missing dimension must not cost us every event, so a middleware
      // that won't register is logged and skipped.
      void analyticsInstance.addSourceMiddleware(({
        payload,
        next
      }) => {
        const pageType = getCurrentPageType();
        if (pageType) {
          const context = payload.obj.context ?? (payload.obj.context = {});
          // Segment types `context.page` as its own five auto-captured fields, so the
          // extra dimension has to be widened past them.
          context.page = {
            ...context.page,
            pageType
          } as typeof context.page;
        }
        next(payload);
      }).catch(middlewareError => {
        logger.warn('Failed to register the page_type middleware', {
          error: middlewareError
        });
      });

      // Not awaited, for the same reason as the page_type middleware above.
      void analyticsInstance.addDestinationMiddleware('Google Tag Manager', gtmDestinationMiddleware).catch(middlewareError => {
        logger.warn('Failed to register the GTM middleware', {
          error: middlewareError
        });
      });

      // Expose analytics instance to window for GTM access
      if (typeof window !== 'undefined') {
        ;
        (window as typeof window & {
          analytics: AnalyticsBrowser;
        }).analytics = analyticsInstance;
      }
    } catch (error) {
      logger.error(error);
      throw error instanceof Error ? error : new Error('Analytics initialization failed');
    }
  })();
  return initPromise;
};

// The tag travels on `context.page.tagRef`, the one place the canonical schema declares it.
// Event-level `context.page` fields win over the SDK's auto-captured page context, and the
// pageType middleware spreads what is already there, so all three coexist.
function buildTagOptions(tagRef: string | undefined): Options | undefined {
  if (!tagRef) {
    return undefined;
  }
  return {
    context: {
      page: {
        tagRef
      } as NonNullable<Options['context']>['page']
    }
  };
}
export function useAnalytics() {
  // Non-throwing: analytics can be used outside a tag context (e.g. the admin dashboard),
  // where there is no tagRef.
  const tagRef = useConfigProviderSafe()?.tagRef;
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const initRef = useRef(false);

  // Initialize analytics once globally
  useEffect(() => {
    if (initRef.current || typeof window === 'undefined') {
      return;
    }
    initRef.current = true;
    const initializeAnalytics = async () => {
      try {
        await initializeAnalyticsOnce();
        setIsReady(true);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Analytics initialization failed'));
      }
    };
    initializeAnalytics();
  }, []);

  // Analytics methods. `track` returns the in-flight delivery so a caller that is
  // about to navigate can wait for it — see `trackBeforeNavigating`. Callers that
  // are not navigating can ignore it.
  const track = useCallback((event: string, properties?: AnalyticsProperties): Promise<unknown> => {
    if (!isReady || !analyticsInstance) {
      return Promise.resolve();
    }

    // A static branch the production build removes, taking the schemas with it.
    if (process.env.NODE_ENV !== 'production') {
      void import('../utils/validateEventPayload').then(({
        validateEventPayload
      }) => validateEventPayload(event, properties ?? {}));
    }
    return Promise.resolve(analyticsInstance.track(event, properties ?? {}, buildTagOptions(tagRef)));
  }, [isReady, tagRef]);
  const page = useCallback((category?: string, name?: string, properties_0?: AnalyticsProperties) => {
    if (!isReady || !analyticsInstance) {
      return;
    }
    analyticsInstance.page(category, name, properties_0 ?? {}, buildTagOptions(tagRef));
  }, [isReady, tagRef]);
  const identify = useCallback((userId: string, traits?: AnalyticsTraits) => {
    if (!isReady || !analyticsInstance) {
      return;
    }
    analyticsInstance.identify(userId, traits ?? {}, buildTagOptions(tagRef));
  }, [isReady, tagRef]);
  const group = useCallback((groupId: string, traits_0?: AnalyticsTraits) => {
    if (!isReady || !analyticsInstance) {
      return;
    }
    analyticsInstance.group(groupId, traits_0 ?? {}, buildTagOptions(tagRef));
  }, [isReady, tagRef]);
  const alias = useCallback((newId: string) => {
    if (!isReady || !analyticsInstance) {
      return;
    }
    analyticsInstance.alias(newId);
  }, [isReady]);
  const reset = useCallback(() => {
    if (!isReady || !analyticsInstance) {
      return;
    }
    analyticsInstance.reset();
  }, [isReady]);

  // The user id Segment persisted, possibly from an earlier visit.
  const getStoredUserId = useCallback(async (): Promise<string | null> => {
    if (!isReady || !analyticsInstance) {
      return null;
    }
    const user = await analyticsInstance.user();
    return user.id() ?? null;
  }, [isReady]);
  return {
    track,
    page,
    identify,
    group,
    alias,
    reset,
    getStoredUserId,
    isReady,
    error
  };
}